import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Search, Edit2, Power, Loader2, Globe, Save, X, Sparkles, Download } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { useForm } from 'react-hook-form';

interface StateRow {
  id: number; name: string; stateCode?: string; gstStateCode?: string;
  isMetro: boolean; isActive: boolean;
  _count?: { cities: number; pincodes: number; branches: number };
  /** Who loaded this row. Null on everything imported before the column existed. */
  createdById?: number | null; createdByName?: string | null;
}

export function StatesPanel() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<StateRow | null>(null);

  const { data: rows, isLoading } = useQuery({
    queryKey: ['states-master', search],
    queryFn: async () => {
      const params: any = {};
      if (search) params.search = search;
      return (await api.get<StateRow[]>('/states-master', { params })).data;
    },
  });

  const toggle = useMutation({
    mutationFn: async (id: number) => (await api.patch(`/states-master/${id}/toggle`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['states-master'] }); toast.success('Status updated'); },
  });

  const cleanup = useMutation({
    mutationFn: async () => (await api.post('/admin/cleanup-cities')).data,
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['cities'] });
      toast.success(`Deactivated ${r.deactivated} garbage cities (e.g. purely numeric / 1-char names)`);
      if (r.sample?.length) {
        console.log('Sample deactivated:', r.sample);
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Cleanup failed'),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 min-w-[260px]">
          <Search size={14} className="text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search state name..."
            className="flex-1 bg-transparent outline-none text-sm" />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => downloadBlob(`/states-master/export${search ? `?search=${encodeURIComponent(search)}` : ''}`, `states-${new Date().toISOString().slice(0,10)}.csv`)}
            className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 flex items-center gap-2">
            <Download size={14} /> Export
          </button>
          <button onClick={() => { if (confirm('Deactivate cities with purely-numeric or 1-char names? (e.g. "66", "92 Sharif")')) cleanup.mutate(); }}
            disabled={cleanup.isPending}
            className="px-3 py-2 border border-amber-300 text-amber-700 text-sm rounded-lg hover:bg-amber-50 flex items-center gap-2 disabled:opacity-60">
            {cleanup.isPending ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Auto-cleanup Cities
          </button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-100 text-xs text-slate-500">
          Total: <b>{rows?.length ?? 0}</b> states · Includes legacy ids 114 (Uttaranchal), 116 (ODISHA), 117 (KHARSIA) for back-compat with old orders.
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 w-16">ID</th>
              <th className="text-left px-3 py-2">State</th>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">GST Code</th>
              <th className="text-left px-3 py-2">Cities</th>
              <th className="text-left px-3 py-2">Pincodes</th>
              <th className="text-left px-3 py-2">Branches</th>
              <th className="text-left px-3 py-2">Uploaded By</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={10} className="text-center py-8 text-slate-400"><Loader2 size={20} className="mx-auto animate-spin" /></td></tr>}
            {!isLoading && (rows?.length ?? 0) === 0 && (
              <tr><td colSpan={10} className="text-center py-8 text-slate-400"><Globe size={20} className="mx-auto mb-1 opacity-50" /> No states found.</td></tr>
            )}
            {rows?.map(s => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{s.id}</td>
                <td className="px-3 py-2 font-medium">{s.name || <span className="text-slate-400 italic">(unnamed)</span>}</td>
                <td className="px-3 py-2 text-slate-600 font-mono text-xs">{s.stateCode ?? '—'}</td>
                <td className="px-3 py-2 text-slate-600 font-mono text-xs">{s.gstStateCode ?? '—'}</td>
                <td className="px-3 py-2 text-slate-600 text-xs">{s._count?.cities ?? 0}</td>
                <td className="px-3 py-2 text-slate-600 text-xs">{s._count?.pincodes ?? 0}</td>
                <td className="px-3 py-2 text-slate-600 text-xs">{s._count?.branches ?? 0}</td>
                <td className="px-3 py-2 text-slate-600 text-xs">{s.createdByName ?? <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center gap-1 text-xs font-medium ${s.isActive ? 'text-success' : 'text-slate-400'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${s.isActive ? 'bg-success' : 'bg-slate-300'}`} />
                    {s.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button onClick={() => setEditing(s)} className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/10 rounded"><Edit2 size={14} /></button>
                  <button onClick={() => toggle.mutate(s.id)}
                    className={`p-1.5 rounded ${s.isActive ? 'text-amber-600 hover:bg-amber-50' : 'text-success hover:bg-success/10'}`}>
                    <Power size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title="Edit State" subtitle={editing?.name} maxWidth="md">
        {editing && <StateEditForm stateRow={editing} onSuccess={() => setEditing(null)} />}
      </Modal>
    </div>
  );
}

function StateEditForm({ stateRow, onSuccess }: { stateRow: StateRow; onSuccess: () => void }) {
  const qc = useQueryClient();
  const { register, handleSubmit } = useForm({ defaultValues: stateRow });
  const save = useMutation({
    mutationFn: async (d: any) => (await api.patch(`/states-master/${stateRow.id}`, d)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['states-master'] }); qc.invalidateQueries({ queryKey: ['states'] }); toast.success('State updated'); onSuccess(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });
  return (
    <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-3">
      <Field label="State Name *">
        <input {...register('name', { required: true })} className="input" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="State Code (e.g. UP, MH)">
          <input {...register('stateCode')} maxLength={5} className="input font-mono uppercase" />
        </Field>
        <Field label="GST State Code (e.g. 09)">
          <input {...register('gstStateCode')} maxLength={5} className="input font-mono" />
        </Field>
      </div>
      <div className="flex items-center gap-4 text-sm text-slate-700">
        <label className="flex items-center gap-2"><input type="checkbox" {...register('isMetro')} className="rounded" /> Metro</label>
        <label className="flex items-center gap-2"><input type="checkbox" {...register('isActive')} className="rounded" /> Active</label>
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t">
        <button type="button" onClick={onSuccess} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg flex items-center gap-1"><X size={14} /> Cancel</button>
        <button type="submit" disabled={save.isPending} className="px-4 py-2 bg-primary text-white text-sm rounded-lg disabled:opacity-60 flex items-center gap-2">
          {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
